
import type { Placement } from '@popperjs/core'
import type { SingleTarget, Instance as TippyInstance } from 'tippy.js'
import type { PropType, Ref } from 'vue'

import tippy from 'tippy.js'
import { defineComponent, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue'

export type TooltipEvent = 'mouseenter' | 'focus' | 'click' | 'manual'

export default defineComponent({
  name: 'EpbTooltip',
  props: {
    target: Element as PropType<SingleTarget>,
    content: {
      type: [String, undefined],
    },
    position: {
      type: String as PropType<Placement>,
      default: 'bottom', // Note: don't use VariationPlacement (e.g. bottom-start) with showArrow to avoid possible errors
    },
    openOn: {
      type: Array as PropType<Array<TooltipEvent>>,
      default: () => ['mouseenter', 'focus'],
    },
    openDelay: {
      type: Number,
      default: 0,
    },
    showArrow: Boolean,
    maxWidth: Number,
  },
  setup(props) {
    const baseClass = 'epb-tooltip'

    const tippyRef: Ref<TippyInstance | undefined> = ref(undefined)

    const instance = getCurrentInstance()

    onBeforeUnmount(() => {
      resetTooltip()
    })

    function renderTooltip(): void {
      const targetEl = props.target ?? instance.proxy.$parent.$el

      if (targetEl) {
        resetTooltip()
        tippyRef.value = tippy(targetEl, {
          animation: 'epb',
          appendTo: document.body,
          arrow: props.showArrow,
          content: props.content ?? instance.proxy.$el,
          delay: [props.openDelay, 0],
          duration: [250, 250],
          interactive: true,
          ...(props.maxWidth && { maxWidth: props.maxWidth }),
          offset: [0, props.showArrow ? 15 : 4],
          popperOptions: {
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  altAxis: true,
                },
              },
            ],
          },
          placement: props.position,
          theme: 'epb',
          trigger: props.openOn.join(' '),
        })
      }
    }

    function resetTooltip(): void {
      if (tippyRef.value) {
        tippyRef.value.destroy()
        tippyRef.value = undefined
      }
    }

    watch(
      () => [props.target, props.content],
      (): void => {
        renderTooltip()
      },
      { immediate: true },
    )

    return {
      baseClass,
    }
  },
})
